import { ChangeDetectionStrategy, Component, Inject, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import * as pdfjsLib from 'pdfjs-dist';
import printJS from 'print-js';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export interface ModalCvPreviewData {
  name: string;
  blob: Blob;
  title?: string;
  description?: string;
  onConfirm?: () => void;
}

@Component({
  selector: 'app-modal-cv-preview',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatIcon
  ],
  templateUrl: './modal-cv-preview.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalCvPreviewComponent {
  readonly isLoading = signal(true);

  pdfPages: WritableSignal<Array<{ imageUrl: string }>> = signal([]);

  private blobUrl: string = '';

  get isConfirmMode(): boolean {
    return !!this.data.onConfirm;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalCvPreviewData,
    private readonly dialogRef: MatDialogRef<ModalCvPreviewComponent>,
    private readonly iconRegistry: MatIconRegistry,
    private readonly sanitizer: DomSanitizer,
  ) {
    this.loadPdf(data.blob);
    this.iconRegistry.addSvgIcon(
      'download-icon',
      this.sanitizer.bypassSecurityTrustResourceUrl('icons/download_icon.svg'),
    );
    this.iconRegistry.addSvgIcon(
      'close-icon',
      this.sanitizer.bypassSecurityTrustResourceUrl('icons/close_icon.svg'),
    );
  }

  private loadPdf(blob: Blob): void {
    this.blobUrl = URL.createObjectURL(blob);
    const loadingTask = pdfjsLib.getDocument(this.blobUrl);

    loadingTask.promise.then((pdf) => {
      const pages: Array<{ imageUrl: string }> = new Array(pdf.numPages);
      let renderedCount = 0;

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        pdf.getPage(pageNumber).then((page) => {
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          page.render({ canvas, viewport }).promise.then(() => {
            pages[pageNumber - 1] = { imageUrl: canvas.toDataURL('image/png') };
            renderedCount++;

            if (renderedCount === pdf.numPages) {
              this.pdfPages.set(pages);
              this.isLoading.set(false);
            }
          });
        });
      }
    });
  }

  confirm(): void {
    this.data.onConfirm?.();
    this.dialogRef.close();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  printPdf(): void {
    printJS({ printable: this.blobUrl, type: 'pdf', documentTitle: this.data.name });
  }

  downloadPdf(): void {
    const a = document.createElement('a');
    a.href = this.blobUrl;
    a.download = `${this.data.name}.pdf`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
